import { Stack, Box } from '@mui/material';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  DragOverEvent,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin,
} from '@dnd-kit/core';
import { useState } from 'react';
import { BoardGrid } from './board-grid';
import { BoardControls } from './controls';
import { BoardHeader } from './header';
import { useBoardContext } from '../hooks/useBoardContext';
import { MappedNoteCard } from '../../card';
import { isCardIntersecting } from '../utils/card-coordinates';
import { CellCoordinates } from '../types';
import { BoardViewMode, CARD_ROWS_SPAN, CELL_CARD_GAP, CELL_HEIGHT, CELL_WIDTH } from '../config';
import { createSnapToPosition } from '../modifiers';
import { isCellId } from '../utils';
import { Sidebar } from './sidebar';
import { useNotesStore } from '../../../stores/notes.store';
import { ListView } from './list-view';

export function Board() {
  const {
    setDraggingCardId,
    draggingCardData,
    moveNoteToGrid,
    moveNoteToBacklog,
    gridData,
    viewMode,
  } = useBoardContext();
  const notes = useNotesStore((state) => state.notes);
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 10,
    },
  });
  const [overElement, setOverElement] = useState<'cell' | 'backlog' | null>(null);

  const sensors = useSensors(pointerSensor);

  const handleDragStart = (event: DragStartEvent) => {
    setDraggingCardId(event.active.id.toString());
  };

  const snapToCorner = createSnapToPosition(({ height, width }) => ({
    x: width / 10,
    y: height / 10,
  }));

  const handleDragEnd = (event: DragEndEvent) => {
    setDraggingCardId(null);

    const { over } = event;
    if (!over) return;

    if (isCellId(over.id.toString())) {
      const { row, column, group } = over.data.current.coordinates as CellCoordinates;

      let coordinates: CellCoordinates = { row, column, group };

      // Handle cell overflowing grid container
      if (gridData.timeSlots.length - CARD_ROWS_SPAN < row) {
        coordinates = { ...coordinates, row: gridData.timeSlots.length - CARD_ROWS_SPAN };
      }

      if (
        isCardIntersecting(
          notes.filter((n) => n.id !== draggingCardData.id),
          coordinates,
          gridData,
        )
      ) {
        return;
      }

      moveNoteToGrid(draggingCardData.id.toString(), coordinates);
    }

    if (over.id.toString() === 'backlog') {
      moveNoteToBacklog(draggingCardData.id.toString());
    }
  };

  const handleDragOver = (e: DragOverEvent) => {
    const { over } = e;
    if (!over) return;

    if (over.id.toString().startsWith('cell-')) {
      setOverElement('cell');
    }

    if (over.id.toString() === 'backlog') {
      setOverElement('backlog');
    }
  };

  return (
    <Stack sx={{ height: '100%', overflow: 'hidden' }}>
      <BoardHeader />
      <Stack direction="row" sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <DndContext
          collisionDetection={pointerWithin}
          sensors={sensors}
          onDragEnd={handleDragEnd}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
        >
          <DragOverlay modifiers={[snapToCorner]}>
            {draggingCardData ? (
              <MappedNoteCard
                key={draggingCardData.id}
                note={draggingCardData}
                indicatorStyle={overElement === 'cell' ? 'around' : 'side'}
                sx={{
                  width: CELL_WIDTH - CELL_CARD_GAP * 2,
                  height: CELL_HEIGHT * 4 - CELL_CARD_GAP * 2,
                }}
              />
            ) : null}
          </DragOverlay>

          <Box
            sx={{
              height: '100%',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              containerType: 'inline-size',
              containerName: 'board-content',
              width: '100%',
            }}
          >
            <BoardControls />

            {viewMode === BoardViewMode.List ? <ListView /> : <BoardGrid />}
          </Box>

          <Sidebar />
        </DndContext>
      </Stack>
    </Stack>
  );
}
